import type { CfbSchoolCatalogEntry } from '../cfbSchoolCatalogTypes';
import { resolveConventionBrandingUrls } from './cfbBrandingPaths';

export type CfbCatalogIndex = {
  bySchoolId: Map<string, CfbSchoolCatalogEntry>;
  byAlias: Map<string, CfbSchoolCatalogEntry>;
};

function normalizeAliasToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function indexTokensForSchool(school: CfbSchoolCatalogEntry): string[] {
  return [
    school.school_id,
    school.athletic_name,
    school.short_name,
    school.display_name,
    ...school.aliases,
  ]
    .map((token) => normalizeAliasToken(token))
    .filter(Boolean);
}

export function buildCfbCatalogIndex(schools: CfbSchoolCatalogEntry[]): CfbCatalogIndex {
  const bySchoolId = new Map<string, CfbSchoolCatalogEntry>();
  const byAlias = new Map<string, CfbSchoolCatalogEntry>();

  for (const school of schools) {
    bySchoolId.set(school.school_id, school);
    for (const token of indexTokensForSchool(school)) {
      if (!byAlias.has(token)) {
        byAlias.set(token, school);
      }
    }
  }

  return { bySchoolId, byAlias };
}

export function resolveSchoolFromIndex(
  index: CfbCatalogIndex | null | undefined,
  options: { schoolId?: string | null; label?: string | null }
): CfbSchoolCatalogEntry | null {
  if (!index) {
    return null;
  }
  const schoolId = options.schoolId?.trim();
  if (schoolId) {
    return index.bySchoolId.get(schoolId) ?? null;
  }
  const label = options.label?.trim();
  if (!label) {
    return null;
  }
  return index.byAlias.get(normalizeAliasToken(label)) ?? null;
}

export function resolveSchoolBrandingUrls(
  school: CfbSchoolCatalogEntry
): { primary_url: string; icon_url: string } | null {
  if (school.branding?.primary_url && school.branding?.icon_url) {
    return {
      primary_url: school.branding.primary_url,
      icon_url: school.branding.icon_url,
    };
  }
  return resolveConventionBrandingUrls(
    school.school_id,
    school.logo_status,
    school.logo_icon_ext
  );
}
